import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";

import Input from "../../../components/form-item/Input";
import IconButton from "../../../components/button/IconButton";
import RadioCard from "../../../components/form-item/RadioCard";
import TitleBlock from "../../../components/block/TitleBlock";
import ToolTip from "../../../components/tooltip/ToolTip";
import Toggle from "../../../components/form-item/Toggle";
import StatusIndicator, { StatusType } from "../../../components/status-indicator/StatusIndicator";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import {
  useGetProfileSettingsQuery,
  useUpdateProfileSettingsMutation,
  useGetProfileContentQuery,
  usePublishProfileMutation,
} from "../../../app/services/creatorProfile";
import type { ProfileExposureSetting } from "../../../types/profile";

/**
 * 公開範囲の RadioCard。`card_mysite_public_{id}.webp` の画像付き。
 * cardId（open/members/close）は BE の exposure_setting（public/private/project）に対応する。
 */
const EXPOSURE_CARDS: {
  cardId: "open" | "members" | "close";
  value: ProfileExposureSetting;
  label: string;
  desc: string;
}[] = [
  { cardId: "open", value: "public", label: "だれでも見れる", desc: "すべての訪問者にプロフィールが公開されます。" },
  { cardId: "members", value: "private", label: "ログインユーザーにだけ見れる", desc: "ログインしているユーザーだけにプロフィールが公開されます。" },
  { cardId: "close", value: "project", label: "プロジェクト関係者のみ", desc: "関係するプロジェクトのメンバーにのみプロフィールが公開されます。" },
];

const DEBOUNCE_MS = 600;

/**
 * マイサイトの公開範囲 / URL（slug）設定 + 公開・非公開トグル。
 * `/api/me/pages/profile/settings` と `/api/me/pages/profile/publish` に接続し、
 * マイページ同様リアルタイム保存する（slug はデバウンス、公開範囲は即時）。
 */
const MySitePublic: React.FC = () => {
  const { data: settingsResp } = useGetProfileSettingsQuery();
  const { data: contentResp, refetch: refetchContent } = useGetProfileContentQuery();
  const [updateSettings] = useUpdateProfileSettingsMutation();
  const [publishProfile, { isLoading: isPublishing }] = usePublishProfileMutation();
  const { copy } = useCopyToClipboard({ successMessage: "URL をコピーしました" });

  const [slug, setSlug] = useState("");
  const [exposure, setExposure] = useState<ProfileExposureSetting>("project");
  const [status, setStatus] = useState<StatusType>("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    const s = settingsResp?.data;
    if (s && !hydratedRef.current) {
      setSlug(s.vanity_slug || "");
      setExposure(s.exposure_setting || "project");
      hydratedRef.current = true;
    }
  }, [settingsResp]);

  const isPublished = contentResp?.data?.is_published ?? false;
  // 共有用の公開 URL（QR / コピー用）。本番 vanity URL。
  const publicUrl = slug ? `https://soremo.jp/${slug}` : "";

  const save = useCallback(
    async (patch: { vanity_slug?: string; exposure_setting?: ProfileExposureSetting }) => {
      try {
        setStatus("saving");
        await updateSettings(patch).unwrap();
        setStatus("saved");
        setTimeout(() => setStatus("idle"), 1500);
        // slug を空にすると BE が強制的に非公開へ落とす（slug 無しの公開は
        // 到達不能なため）。公開状態の表示を同期し、公開中だった場合は通知する。
        if (patch.vanity_slug !== undefined && !patch.vanity_slug.trim()) {
          if (isPublished) {
            toast("URL（slug）が削除されたため、プロフィールを非公開にしました");
          }
          refetchContent();
        }
      } catch {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 2000);
      }
    },
    [updateSettings, isPublished, refetchContent],
  );

  const handleSlugChange = (value: string) => {
    setSlug(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save({ vanity_slug: value }), DEBOUNCE_MS);
  };

  const handleExposureChange = (value: ProfileExposureSetting) => {
    setExposure(value);
    save({ exposure_setting: value });
  };

  const handleTogglePublish = async () => {
    try {
      await publishProfile(!isPublished).unwrap();
      toast.success(!isPublished ? "プロフィールを公開しました" : "プロフィールを非公開にしました");
    } catch (err) {
      // slug 未設定で公開しようとした場合など、BE の理由メッセージを優先表示。
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(message || "公開状態の更新に失敗しました");
    }
  };

  return (
    <div className="u-col u-gap16">
      <TitleBlock title="マイサイトの公開範囲とURL" description="サイトの公開に関するカスタマイズ" />

      <div className="u-col u-w100 u-gap8">
        <p className="bodytext-13">公開 / 非公開を切り替えます。</p>
        <div onClick={handleTogglePublish} style={{ pointerEvents: isPublishing ? "none" : "auto" }}>
          <Toggle value={isPublished}>{isPublished ? "公開中" : "非公開"}</Toggle>
        </div>
        {/* slug 未設定では公開できない（BE が 400 を返す）ため先に案内する。 */}
        {!isPublished && !slug.trim() && (
          <span className="bodytext-11 u-text-light-gray">
            公開するには URL（slug）の設定が必要です。
          </span>
        )}
      </div>

      {/* URL（slug）は非公開中でも設定できる（公開の前提条件のため常時表示）。 */}
      <div className="u-col u-w100 u-gap8">
        <p className="bodytext-13">固有の URL（slug）を設定できます。入力は自動保存されます。</p>
        <div className="u-row u-gap8 u-w100">
          <span className="bodytext-13 u-text-light-gray">soremo.jp/</span>
          <Input type="text" value={slug} onChange={(e) => handleSlugChange(e.target.value)} maxlength={50} width="240px" />
          <ToolTip description="公開 URL をコピー" placement="top">
            <IconButton icon="content_copy" onClick={() => copy(publicUrl)} disabled={!publicUrl} />
          </ToolTip>
        </div>
      </div>

      {/* 公開中のときだけ QR・公開範囲を表示する（非公開時は機能しないため隠す）。 */}
      {isPublished && (
        <>
          <div className="u-col u-w100 u-gap8">
            {/* 公開 URL の QR コード。読み取り・クリックの両方で公開ページを新規タブで開く。 */}
            {publicUrl ? (
              <ToolTip description="QR を読み取る / クリックで公開ページを開く" placement="top">
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignSelf: "flex-start",
                    padding: 8,
                    background: "#fff",
                    borderRadius: 4,
                    border: "1px solid var(--soremo-border)",
                  }}
                >
                  <QRCodeSVG value={publicUrl} size={120} />
                </a>
              </ToolTip>
            ) : (
              <span className="bodytext-11 u-text-light-gray">
                slug を入力すると QR コードを生成します。
              </span>
            )}
          </div>

          <div className="u-col u-w100 u-gap8">
            <p className="bodytext-13">公開範囲設定</p>
            <div className="u-col u-w100 u-gap16">
              {EXPOSURE_CARDS.map((card) => (
                <RadioCard
                  key={card.cardId}
                  name="mysite_public"
                  id={card.cardId}
                  width="128px"
                  height="128px"
                  showRadio
                  showImage
                  checked={exposure === card.value}
                  onClick={() => handleExposureChange(card.value)}
                >
                  <label>{card.label}</label>
                  <p>{card.desc}</p>
                </RadioCard>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="u-row-end u-w100">
        <StatusIndicator status={status} inline />
      </div>
    </div>
  );
};

export default MySitePublic;
